/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState, useCallback, useRef } from 'react';
import { api, getCurrentUser, getSessionId } from '@/lib/api';
import { DigestCards, PresencePanel, AlertsPanel, ActivityPanel, CountriesPanel, SecuritySettings, useOracleLive } from '@/components/oracle/OraclePanels';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Eye, RefreshCw, Search, ShieldCheck, ShieldX, LogIn, KeyRound, ShieldAlert,
  Ban, Radar, Users, ChevronLeft, ChevronRight, Activity,
  Globe, MapPin, Monitor, Filter, Network, Clock as ClockIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { Switch } from '@/components/ui/switch';

const EVENT_META = {
  login_success: { label: 'Connexion réussie', cls: 'bg-emerald-50 border-emerald-200 text-emerald-700', Icon: LogIn },
  login_failed: { label: 'Échec login', cls: 'bg-amber-50 border-amber-200 text-amber-700', Icon: ShieldX },
  login_blocked: { label: 'IP bloquée', cls: 'bg-red-50 border-red-200 text-red-700', Icon: Ban },
  stepup_success: { label: 'PIN Batcave OK', cls: 'bg-blue-50 border-blue-200 text-blue-700', Icon: KeyRound },
  stepup_failed: { label: 'PIN Batcave échec', cls: 'bg-orange-50 border-orange-200 text-orange-700', Icon: KeyRound },
  ndd_intrusion: { label: 'Intrusion domaine', cls: 'bg-purple-50 border-purple-200 text-purple-700', Icon: Radar },
  ip_banned: { label: 'IP bannie', cls: 'bg-red-50 border-red-200 text-red-700', Icon: Ban },
  ip_unbanned: { label: 'IP débannie', cls: 'bg-zinc-50 border-zinc-200 text-zinc-600', Icon: ShieldCheck },
  auto_ban: { label: 'Ban automatique', cls: 'bg-red-100 border-red-300 text-red-800', Icon: ShieldAlert },
  login_new_device: { label: 'Nouvel appareil', cls: 'bg-orange-50 border-orange-200 text-orange-700', Icon: ShieldAlert },
  login_global_lock: { label: 'Verrou global', cls: 'bg-red-100 border-red-300 text-red-800', Icon: Ban },
  login_wrong_zone: { label: 'Mauvaise entrée', cls: 'bg-amber-50 border-amber-200 text-amber-700', Icon: ShieldX },
  login_disabled: { label: 'Compte désactivé', cls: 'bg-zinc-100 border-zinc-300 text-zinc-700', Icon: Ban },
  login_country_blocked: { label: 'Pays bloqué', cls: 'bg-red-50 border-red-200 text-red-700', Icon: Ban },
  session_revoked: { label: 'Session révoquée', cls: 'bg-blue-50 border-blue-200 text-blue-700', Icon: KeyRound },
  invite_pin_set: { label: 'PIN créé (invitation)', cls: 'bg-emerald-50 border-emerald-200 text-emerald-700', Icon: KeyRound },
  user_disabled: { label: 'Accès désactivé', cls: 'bg-zinc-100 border-zinc-300 text-zinc-700', Icon: Ban },
};
const evMeta = (e) => EVENT_META[e] || { label: e, cls: 'bg-zinc-50 border-zinc-200 text-zinc-600', Icon: Activity };

const PRESETS = [
  { id: '24h', label: '24 h', days: 1 },
  { id: '7d', label: '7 jours', days: 7 },
  { id: '30d', label: '30 jours', days: 30 },
  { id: 'all', label: 'Tout', days: 0 },
];
const PAGE = 50;

function sinceFromPreset(days) {
  if (!days) return '';
  const d = new Date(Date.now() - days * 86400000);
  return d.toISOString();
}

const StatCard = ({ icon: Icon, label, value, accent, testid }) => (
  <Card className="card-flat p-5" data-testid={testid}>
    <div className="flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-wider mb-2">
      <Icon className={`h-4 w-4 ${accent || ''}`} /> {label}
    </div>
    <div className={`font-display text-3xl font-bold tracking-tight ${accent || ''}`}>{value}</div>
  </Card>
);

// "path=... · ref=... · ua=..." -> { path, ref, ua }
function parseDetail(detail) {
  const out = {};
  if (!detail) return out;
  detail.split(' · ').forEach((part) => {
    const eq = part.indexOf('=');
    if (eq > 0) out[part.slice(0, eq).trim()] = part.slice(eq + 1).trim();
  });
  return out;
}

function parseUA(ua) {
  if (!ua) return null;
  let browser = 'Inconnu';
  let os = 'Inconnu';
  let device = 'Ordinateur';
  if (/curl|wget|python|axios|go-http|java\//i.test(ua)) browser = 'Script / bot';
  else if (/bot|spider|crawl|preview|facebookexternalhit|headless/i.test(ua)) browser = 'Bot / crawler';
  else if (/Edg/i.test(ua)) browser = 'Edge';
  else if (/OPR|Opera/i.test(ua)) browser = 'Opera';
  else if (/Chrome/i.test(ua)) browser = 'Chrome';
  else if (/Firefox/i.test(ua)) browser = 'Firefox';
  else if (/Safari/i.test(ua)) browser = 'Safari';
  if (/Windows/i.test(ua)) os = 'Windows';
  else if (/Android/i.test(ua)) { os = 'Android'; device = 'Mobile'; }
  else if (/iPhone/i.test(ua)) { os = 'iOS'; device = 'Mobile'; }
  else if (/iPad/i.test(ua)) { os = 'iPadOS'; device = 'Tablette'; }
  else if (/Mac OS X|Macintosh/i.test(ua)) os = 'macOS';
  else if (/Linux/i.test(ua)) os = 'Linux';
  if (/Mobile/i.test(ua) && device === 'Ordinateur') device = 'Mobile';
  return { browser, os, device };
}

const Field = ({ label, value, mono, testid }) => (
  <div data-testid={testid}>
    <div className="text-[11px] uppercase tracking-wider text-zinc-400 mb-0.5">{label}</div>
    <div className={`text-sm text-zinc-800 break-words ${mono ? 'font-mono' : ''}`}>{value || '—'}</div>
  </div>
);

// Threat level → colour + French label used across the IP profile.
const THREAT = {
  critical: { label: 'Menace critique', badge: 'bg-red-600 text-white', ring: 'text-red-600', bar: 'bg-red-600' },
  high: { label: 'Risque élevé', badge: 'bg-orange-500 text-white', ring: 'text-orange-500', bar: 'bg-orange-500' },
  medium: { label: 'À surveiller', badge: 'bg-amber-400 text-amber-950', ring: 'text-amber-500', bar: 'bg-amber-400' },
  low: { label: 'Risque faible', badge: 'bg-emerald-500 text-white', ring: 'text-emerald-500', bar: 'bg-emerald-500' },
  info: { label: 'Inoffensif', badge: 'bg-zinc-200 text-zinc-600', ring: 'text-zinc-400', bar: 'bg-zinc-300' },
};
const SEV_CLS = {
  critical: 'text-red-600', high: 'text-orange-600', medium: 'text-amber-600', info: 'text-zinc-500',
};
function fmtDuration(s) {
  if (!s) return 'instantané';
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}min ${s % 60}s`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}min`;
  return `${Math.floor(s / 86400)}j ${Math.floor((s % 86400) / 3600)}h`;
}

function IpProfileDialog({ ip, onClose, onBan, onUnban, banBusy }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!ip) { setData(null); return; }
    setLoading(true);
    api.get(`/security/ip-profile/${encodeURIComponent(ip)}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error('Impossible de charger le profil'))
      .finally(() => setLoading(false));
  }, [ip]);

  const t = data?.threat ? (THREAT[data.threat.level] || THREAT.info) : THREAT.info;

  return (
    <Dialog open={!!ip} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="ip-profile-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2">
            <Network className="h-5 w-5" /> Profil & parcours de l'IP
          </DialogTitle>
        </DialogHeader>

        {loading || !data ? (
          <div className="py-16 text-center text-zinc-400"><RefreshCw className="h-6 w-6 animate-spin inline" /></div>
        ) : (
          <div className="space-y-5 max-h-[70vh] overflow-y-auto pr-1">
            {/* Threat verdict */}
            <div className="rounded-xl border border-zinc-200 p-4 flex items-center gap-4" data-testid="ip-profile-verdict">
              <div className="relative shrink-0 w-20 h-20 grid place-items-center">
                <svg className="w-20 h-20 -rotate-90" viewBox="0 0 40 40">
                  <circle cx="20" cy="20" r="16" fill="none" stroke="#f1f1f3" strokeWidth="4" />
                  <circle cx="20" cy="20" r="16" fill="none" strokeWidth="4" strokeLinecap="round"
                    className={t.ring} stroke="currentColor"
                    strokeDasharray={`${(data.threat.score / 100) * 100.5} 100.5`} />
                </svg>
                <div className="absolute text-center">
                  <div className="text-xl font-bold leading-none">{data.threat.score}</div>
                  <div className="text-[9px] text-zinc-400 uppercase">/ 100</div>
                </div>
              </div>
              <div className="min-w-0">
                <span className={`inline-block px-2.5 py-1 rounded-md text-sm font-semibold ${t.badge}`} data-testid="ip-profile-level">{t.label}</span>
                <div className="font-mono text-sm text-zinc-800 mt-2">{data.ip}</div>
                <div className="text-xs text-zinc-500">
                  {data.geo?.available ? `${data.geo.flag || ''} ${data.geo.city || ''}${data.geo.city ? ', ' : ''}${data.geo.country || ''} · ${data.geo.isp || ''}` : 'Géolocalisation indisponible'}
                </div>
              </div>
              <div className="ml-auto shrink-0">
                {data.banned ? (
                  <Button variant="outline" size="sm" className="rounded-md" disabled={banBusy} onClick={async () => { await onUnban(data.ip); setData((d) => ({ ...d, banned: false })); }} data-testid="ip-profile-unban">Débannir</Button>
                ) : (
                  <Button size="sm" className="rounded-md bg-red-600 hover:bg-red-700 text-white" disabled={banBusy} onClick={async () => { await onBan(data.ip); setData((d) => ({ ...d, banned: true })); }} data-testid="ip-profile-ban"><Ban className="h-4 w-4 mr-1.5" /> Bannir</Button>
                )}
              </div>
            </div>

            {/* Metrics */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                ['Requêtes', data.total_events],
                ['Pages distinctes', data.distinct_path_count],
                ['Durée', fmtDuration(data.duration_seconds)],
                ['Cadence', `${data.rate_per_min}/min`],
              ].map(([l, v]) => (
                <div key={l} className="rounded-lg bg-zinc-50 border border-zinc-100 p-2.5 text-center">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-400">{l}</div>
                  <div className="font-bold text-sm mt-0.5">{v}</div>
                </div>
              ))}
            </div>

            {/* Why this verdict */}
            <div>
              <div className="text-[11px] uppercase tracking-wider text-zinc-400 mb-2 font-semibold">— Analyse ({data.threat.reasons.length} signal{data.threat.reasons.length > 1 ? 'aux' : ''})</div>
              <div className="space-y-1.5" data-testid="ip-profile-reasons">
                {data.threat.reasons.map((r, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm">
                    <span className={`mt-1 h-1.5 w-1.5 rounded-full shrink-0 ${(THREAT[r.severity] || THREAT.info).bar}`} />
                    <span className={`${SEV_CLS[r.severity] || 'text-zinc-600'}`}>{r.text}</span>
                    {r.points > 0 && <span className="ml-auto text-[11px] font-mono text-zinc-300 shrink-0">+{r.points}</span>}
                  </div>
                ))}
                {data.is_bot_ua && <div className="text-xs text-zinc-400 pt-1">UA : {data.user_agents.join(' · ')}</div>}
              </div>
            </div>

            {/* Journey timeline */}
            <div>
              <div className="text-[11px] uppercase tracking-wider text-zinc-400 mb-2 font-semibold">— Parcours ({data.steps.length} étapes, plus récent en haut)</div>
              <div className="border-l-2 border-zinc-100 pl-3 space-y-2.5" data-testid="ip-profile-journey">
                {data.steps.map((s, i) => {
                  const m = evMeta(s.event);
                  return (
                    <div key={i} className="relative" data-testid="ip-journey-step">
                      <span className="absolute -left-[17px] top-1 h-2 w-2 rounded-full bg-zinc-300 ring-2 ring-white" />
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-[11px] text-zinc-400">{(s.created_at || '').replace('T', ' ').slice(0, 19)}</span>
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-medium ${m.cls}`}><m.Icon className="h-3 w-3" />{m.label}</span>
                      </div>
                      {s.path && <div className="font-mono text-xs text-zinc-800 break-all mt-0.5">{s.path}</div>}
                      {s.ref && <div className="text-[11px] text-zinc-400">← provenance : {s.ref}</div>}
                      {!s.path && s.detail && <div className="text-[11px] text-zinc-400">{s.detail}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function EventDetailDialog({ item, onClose, onFilterIp, ipInfo, ipLoading, onBan, onUnban, banBusy, onOpenProfile }) {
  if (!item) return null;
  const m = evMeta(item.event);
  const parsed = parseDetail(item.detail);
  const ua = parsed.ua || '';
  const uaInfo = parseUA(ua);
  const isIntrusion = item.event === 'ndd_intrusion';
  const canBan = item.ip && item.ip !== 'unknown';
  const banned = ipInfo?.banned;

  return (
    <Dialog open={!!item} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="oracle-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2">
            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border text-sm font-medium ${m.cls}`}>
              <m.Icon className="h-4 w-4" /> {m.label}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 max-h-[65vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Horodatage" value={(item.created_at || '').replace('T', ' ').slice(0, 19)} mono />
            <div>
              <div className="text-[11px] uppercase tracking-wider text-zinc-400 mb-0.5">Adresse IP</div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-mono text-zinc-800" data-testid="oracle-detail-ip">{item.ip || '—'}</span>
                {item.ip && (
                  <button
                    onClick={() => onFilterIp(item.ip)}
                    className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"
                    data-testid="oracle-detail-filter-ip"
                  >
                    <Filter className="h-3 w-3" /> filtrer
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Géolocalisation */}
          <div className="border-t border-zinc-100 pt-4">
            <div className="flex items-center gap-2 text-zinc-700 font-semibold text-sm mb-3">
              <Globe className="h-4 w-4" /> Géolocalisation de l'IP
            </div>
            {ipLoading ? (
              <div className="text-sm text-zinc-400">Localisation en cours…</div>
            ) : !ipInfo || ipInfo.available === false ? (
              <div className="text-sm text-zinc-400">{ipInfo?.note || 'Non disponible'}</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="oracle-detail-geo">
                <Field label="Pays" value={`${ipInfo.flag || ''} ${ipInfo.country || ''} ${ipInfo.country_code ? `(${ipInfo.country_code})` : ''}`.trim()} />
                <Field label="Ville / région" value={[ipInfo.city, ipInfo.region].filter(Boolean).join(', ')} />
                <Field label="Fournisseur (FAI)" value={ipInfo.isp} />
                <Field label="Organisation" value={ipInfo.org} />
                <Field label="ASN" value={ipInfo.asn} mono />
                <Field label="Type" value={ipInfo.type} />
              </div>
            )}
          </div>

          {/* Détails spécifiques */}
          <div className="border-t border-zinc-100 pt-4 space-y-4">
            {isIntrusion ? (
              <>
                <Field label="Page visitée (chemin)" value={parsed.path} mono testid="oracle-detail-path" />
                <Field label="Provenance (referer)" value={parsed.ref && parsed.ref !== '—' ? parsed.ref : 'Accès direct / inconnu'} mono />
                {uaInfo && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    <Field label="Navigateur" value={uaInfo.browser} />
                    <Field label="Système" value={uaInfo.os} />
                    <Field label="Appareil" value={uaInfo.device} />
                  </div>
                )}
                <Field label="User-Agent brut" value={ua} mono />
              </>
            ) : (
              <Field label="Utilisateur / info" value={item.detail} />
            )}
          </div>
        </div>

        {item.ip && item.ip !== 'unknown' && (
          <Button
            variant="outline"
            className="w-full rounded-md border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white"
            onClick={() => onOpenProfile(item.ip)}
            data-testid="oracle-detail-open-profile"
          >
            <Network className="h-4 w-4 mr-2" /> Voir le parcours complet & analyse de menace
          </Button>
        )}

        {canBan && (
          <div className="border-t border-zinc-100 pt-4 flex items-center justify-between gap-3">
            {banned ? (
              <>
                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-red-700">
                  <Ban className="h-4 w-4" /> Cette IP est bannie
                </span>
                <Button variant="outline" className="rounded-md" disabled={banBusy} onClick={() => onUnban(item.ip)} data-testid="oracle-detail-unban">
                  Débannir
                </Button>
              </>
            ) : (
              <>
                <span className="text-xs text-zinc-500">Bloquer définitivement toutes les requêtes de cette IP.</span>
                <Button
                  className="rounded-md bg-red-600 hover:bg-red-700 text-white"
                  disabled={banBusy}
                  onClick={() => onBan(item.ip)}
                  data-testid="oracle-detail-ban"
                >
                  <Ban className="h-4 w-4 mr-1.5" /> Bannir cette IP
                </Button>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Oracle() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [byEvent, setByEvent] = useState({});
  const [uniqueIps, setUniqueIps] = useState(0);
  const [loading, setLoading] = useState(false);

  const [event, setEvent] = useState('all');
  const [ip, setIp] = useState('');
  const [q, setQ] = useState('');
  const [preset, setPreset] = useState('7d');
  const [page, setPage] = useState(0);
  const [auto, setAuto] = useState(false);
  const autoRef = useRef(null);

  const [detail, setDetail] = useState(null);
  const [ipInfo, setIpInfo] = useState(null);
  const [ipLoading, setIpLoading] = useState(false);
  const [geoMap, setGeoMap] = useState({});
  const [banBusy, setBanBusy] = useState(false);
  const [profileIp, setProfileIp] = useState(null);
  const [bannedOpen, setBannedOpen] = useState(false);
  const [autobanOpen, setAutobanOpen] = useState(false);
  const [autoban, setAutoban] = useState({ enabled: true, sensitive_immediate: true, intrusion_threshold: 12, auth_threshold: 8, window_hours: 6 });
  const [autobanSaving, setAutobanSaving] = useState(false);
  const [bannedList, setBannedList] = useState([]);
  const me = { ...getCurrentUser(), sid: getSessionId() };
  const [tab, setTab] = useState('live');
  const [users, setUsers] = useState([]);
  useEffect(() => { api.get('/users').then((r) => setUsers(r.data)).catch(() => {}); }, []);
  const digest = useOracleLive(() => api.get('/security/digest').then((r) => r.data), [], 30000);
  const presence = useOracleLive(() => api.get('/security/presence').then((r) => r.data), [], 15000);
  const alerts = useOracleLive(() => api.get('/security/alerts').then((r) => r.data), [], 20000);
  const killSession = async (s) => { if (!window.confirm(`Déconnecter ${s.name} (${s.ip}) ?`)) return; try { await api.delete(`/security/sessions/${s.sid}`); toast.success('Session fermée'); presence.reload(); } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); } };
  const killUser = async (s) => { if (!window.confirm(`Révoquer TOUTES les sessions de ${s.name} ? Il devra se reconnecter.`)) return; try { const r = await api.delete(`/security/sessions/user/${s.user_id}`); toast.success(`${r.data.revoked} session(s) révoquée(s)`); presence.reload(); } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); } };
  const alertsSeen = async () => { await api.post('/security/alerts/seen'); alerts.reload(); window.dispatchEvent(new Event('oracle-alerts-seen')); };

  const openDetail = useCallback(async (item) => {
    setDetail(item);
    setIpInfo(null);
    if (!item.ip || item.ip === 'unknown') return;
    setIpLoading(true);
    try {
      const r = await api.get(`/security/ip-info/${encodeURIComponent(item.ip)}`);
      setIpInfo(r.data);
    } catch {
      setIpInfo({ available: false, note: 'Erreur de géolocalisation' });
    } finally {
      setIpLoading(false);
    }
  }, []);

  const filterByIp = (ip) => {
    setDetail(null);
    setIp(ip);
  };

  const banIp = async (ip) => {
    setBanBusy(true);
    try {
      await api.post('/security/ban-ip', { ip });
      toast.success(`IP ${ip} bannie`);
      setIpInfo((p) => ({ ...(p || {}), banned: true }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec du bannissement');
    } finally {
      setBanBusy(false);
    }
  };

  const unbanIp = async (ip) => {
    setBanBusy(true);
    try {
      await api.delete(`/security/ban-ip/${encodeURIComponent(ip)}`);
      toast.success(`IP ${ip} débannie`);
      setIpInfo((p) => ({ ...(p || {}), banned: false }));
      setBannedList((l) => l.filter((b) => b.ip !== ip));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec');
    } finally {
      setBanBusy(false);
    }
  };

  useEffect(() => {
    api.get('/security/autoban-config').then((r) => setAutoban(r.data)).catch(() => {});
  }, []);

  const saveAutoban = async (patch) => {
    const next = { ...autoban, ...patch };
    setAutoban(next);
    setAutobanSaving(true);
    try {
      const { data } = await api.put('/security/autoban-config', patch);
      setAutoban(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de sauvegarde');
    } finally {
      setAutobanSaving(false);
    }
  };

  const openBanned = async () => {
    setBannedOpen(true);
    try {
      const r = await api.get('/security/banned-ips');
      setBannedList(r.data.banned || []);
    } catch {
      setBannedList([]);
    }
  };

  // Resolve geo (flag + country) for the IPs shown in the table (cache-first).
  useEffect(() => {
    const ips = [...new Set(items.map((i) => i.ip).filter((ip) => ip && ip !== 'unknown' && !(ip in geoMap)))];
    if (ips.length === 0) return undefined;
    let active = true;
    api.post('/security/ip-info-batch', { ips })
      .then((r) => { if (active) setGeoMap((prev) => ({ ...prev, ...r.data.geo })); })
      .catch(() => {});
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        event,
        ip: ip.trim(),
        q: q.trim(),
        since: sinceFromPreset(PRESETS.find((p) => p.id === preset)?.days ?? 0),
        limit: PAGE,
        skip: page * PAGE,
      };
      const r = await api.get('/security/audit', { params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setByEvent(r.data.by_event || {});
      setUniqueIps(r.data.unique_ips || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur de chargement du journal');
    } finally {
      setLoading(false);
    }
  }, [event, ip, q, preset, page]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 10s when enabled.
  useEffect(() => {
    if (auto) {
      autoRef.current = setInterval(load, 10000);
    }
    return () => { if (autoRef.current) clearInterval(autoRef.current); };
  }, [auto, load]);

  // Reset to first page when filters change.
  useEffect(() => { setPage(0); }, [event, ip, q, preset]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE));

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-7xl">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="overline mb-2 flex items-center gap-1.5"><Eye className="h-3.5 w-3.5" /> Surveillance</div>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Oracle</h1>
          <p className="text-zinc-600 mt-2">Journal de sécurité — connexions, échecs, IP bloquées et intrusions sur le domaine.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="rounded-md" onClick={() => setAutobanOpen(true)} data-testid="oracle-autoban-button">
            <ShieldAlert className={`h-4 w-4 mr-2 ${autoban.enabled ? 'text-emerald-600' : 'text-zinc-400'}`} /> Bouclier auto
          </Button>
          <Button variant="outline" className="rounded-md" onClick={openBanned} data-testid="oracle-banned-button">
            <Ban className="h-4 w-4 mr-2 text-red-600" /> IP bannies
          </Button>
          <Button
            variant={auto ? 'default' : 'outline'}
            className={`rounded-md ${auto ? 'btn-primary' : ''}`}
            onClick={() => setAuto((a) => !a)}
            data-testid="oracle-auto-toggle"
          >
            <Activity className={`h-4 w-4 mr-2 ${auto ? 'animate-pulse' : ''}`} /> {auto ? 'Live' : 'Live off'}
          </Button>
          <Button variant="outline" className="rounded-md" onClick={load} disabled={loading} data-testid="oracle-refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 mb-6 border-b border-zinc-200" data-testid="oracle-tabs">
        {[['live', 'Présence & alertes'], ['journal', 'Journal de sécurité'], ['activity', 'Actions utilisateurs'], ['geo', 'Pays & protections']].map(([k, l]) => <button key={k} type="button" onClick={() => setTab(k)} aria-pressed={tab === k} className={`px-3 h-10 text-sm font-medium border-b-2 -mb-px transition ${tab === k ? 'border-zinc-950 text-zinc-950' : 'border-transparent text-zinc-500 hover:text-zinc-800'}`} data-testid={`oracle-tab-${k}`}>{l}{k === 'live' && alerts.data?.unread ? <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-red-600 text-white text-[10px] font-mono">{alerts.data.unread}</span> : null}</button>)}
      </div>
      {tab === 'live' && <>
        <DigestCards digest={digest.data} />
        <PresencePanel presence={presence.data} onKill={killSession} onKillUser={killUser} me={me} />
        <AlertsPanel alerts={alerts.data} onSeen={alertsSeen} onOpenIp={(ipv) => setProfileIp(ipv)} />
      </>}
      {tab === 'activity' && <ActivityPanel users={users} />}
      {tab === 'geo' && <><SecuritySettings /><CountriesPanel /></>}
      {tab === 'journal' && <>
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-6">
        <StatCard icon={Activity} label="Événements" value={total} testid="oracle-stat-total" />
        <StatCard icon={LogIn} label="Connexions" value={byEvent.login_success || 0} accent="text-emerald-600" testid="oracle-stat-success" />
        <StatCard icon={ShieldX} label="Échecs" value={byEvent.login_failed || 0} accent="text-amber-600" testid="oracle-stat-failed" />
        <StatCard icon={Ban} label="IP bloquées" value={byEvent.login_blocked || 0} accent="text-red-600" testid="oracle-stat-blocked" />
        <StatCard icon={Radar} label="Intrusions NDD" value={byEvent.ndd_intrusion || 0} accent="text-purple-600" testid="oracle-stat-intrusions" />
        <StatCard icon={Users} label="IP uniques" value={uniqueIps} testid="oracle-stat-ips" />
      </div>

      {/* Filters */}
      <Card className="card-flat p-4 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <Select value={event} onValueChange={setEvent}>
            <SelectTrigger className="w-52 h-10 rounded-md" data-testid="oracle-event-select">
              <SelectValue placeholder="Type d'événement" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les événements</SelectItem>
              {Object.keys(EVENT_META).map((k) => (
                <SelectItem key={k} value={k} data-testid={`oracle-event-${k}`}>{EVENT_META[k].label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              value={ip}
              onChange={(e) => setIp(e.target.value)}
              placeholder="Filtrer par IP"
              className="h-10 rounded-md pl-9 w-44 font-mono text-sm"
              data-testid="oracle-ip-input"
            />
          </div>

          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher (détail, chemin, UA…)"
              className="h-10 rounded-md pl-9 w-64 text-sm"
              data-testid="oracle-search-input"
            />
          </div>

          <div className="flex items-center gap-1 ml-auto">
            {PRESETS.map((p) => (
              <button
                key={p.id}
                onClick={() => setPreset(p.id)}
                data-testid={`oracle-preset-${p.id}`}
                className={`px-3 h-9 rounded-md text-sm font-medium transition ${preset === p.id ? 'bg-zinc-950 text-white' : 'bg-white border border-zinc-200 text-zinc-600 hover:bg-zinc-100'}`}
              >{p.label}</button>
            ))}
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card className="card-flat overflow-hidden">
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full text-sm" data-testid="oracle-table">
            <thead>
              <tr className="text-left text-xs text-zinc-400 uppercase tracking-wider border-b border-zinc-100 bg-zinc-50/50">
                <th className="py-3 px-4 font-medium">Horodatage</th>
                <th className="py-3 px-4 font-medium">Événement</th>
                <th className="py-3 px-4 font-medium">IP</th>
                <th className="py-3 px-4 font-medium">Pays</th>
                <th className="py-3 px-4 font-medium">Détail</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={5} className="py-12 text-center text-zinc-400">{loading ? 'Chargement…' : 'Aucun événement pour ces filtres'}</td></tr>
              ) : items.map((it, i) => {
                const m = evMeta(it.event);
                return (
                  <tr key={i} className="border-b border-zinc-50 hover:bg-zinc-50/50 cursor-pointer" data-testid="oracle-row" onClick={() => openDetail(it)}>
                    <td className="py-2.5 px-4 font-mono text-xs text-zinc-500 whitespace-nowrap">
                      {(it.created_at || '').replace('T', ' ').slice(0, 19)}
                    </td>
                    <td className="py-2.5 px-4">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border text-xs font-medium ${m.cls}`}>
                        <m.Icon className="h-3.5 w-3.5" /> {m.label}
                      </span>
                    </td>
                    <td className="py-2.5 px-4 font-mono text-xs text-zinc-700 whitespace-nowrap">{it.ip || '—'}</td>
                    <td className="py-2.5 px-4 text-xs whitespace-nowrap" data-testid="oracle-geo-cell">
                      {geoMap[it.ip]?.available
                        ? <span title={`${geoMap[it.ip].city || ''} ${geoMap[it.ip].country || ''}`.trim()}>{geoMap[it.ip].flag} {geoMap[it.ip].country_code || geoMap[it.ip].country}</span>
                        : <span className="text-zinc-300">—</span>}
                    </td>
                    <td className="py-2.5 px-4 text-xs text-zinc-500 max-w-[360px]">
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate" title={it.detail}>{it.detail || '—'}</span>
                        {it.ip && it.ip !== 'unknown' && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setProfileIp(it.ip); }}
                            data-testid="oracle-row-profile"
                            title="Parcours & analyse de menace"
                            className="shrink-0 text-zinc-400 hover:text-zinc-900 transition-colors"
                          >
                            <Network className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="sm:hidden divide-y divide-zinc-100" data-testid="oracle-mobile-list">
          {items.length === 0 ? (
            <div className="py-12 text-center text-zinc-400">{loading ? 'Chargement…' : 'Aucun événement pour ces filtres'}</div>
          ) : items.map((it, i) => {
            const m = evMeta(it.event);
            return (
              <button key={i} type="button" onClick={() => openDetail(it)} data-testid="oracle-card" className="w-full text-left px-4 py-3 active:bg-zinc-50">
                <div className="flex items-center justify-between gap-2">
                  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border text-xs font-medium ${m.cls}`}>
                    <m.Icon className="h-3.5 w-3.5" /> {m.label}
                  </span>
                  <span className="text-[11px] font-mono text-zinc-400 shrink-0">{(it.created_at || '').replace('T', ' ').slice(5, 19)}</span>
                </div>
                <div className="mt-1.5 flex items-center gap-2 text-xs">
                  <span className="font-mono text-zinc-700">{it.ip || '—'}</span>
                  {geoMap[it.ip]?.available && <span className="text-zinc-500">{geoMap[it.ip].flag} {geoMap[it.ip].country_code || geoMap[it.ip].country}</span>}
                  {it.ip && it.ip !== 'unknown' && (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); setProfileIp(it.ip); }}
                      data-testid="oracle-card-profile"
                      className="ml-auto inline-flex items-center gap-1 text-zinc-900 font-medium"
                    >
                      <Network className="h-3.5 w-3.5" /> Parcours
                    </span>
                  )}
                </div>
                {it.detail && <div className="mt-1 text-[11px] text-zinc-400 truncate">{it.detail}</div>}
              </button>
            );
          })}
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-100">
          <div className="text-xs text-zinc-500">{total} événement(s) · page {page + 1}/{totalPages}</div>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" className="rounded-md h-8 w-8" disabled={page === 0 || loading}
              onClick={() => setPage((p) => Math.max(0, p - 1))} data-testid="oracle-prev">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" className="rounded-md h-8 w-8" disabled={page + 1 >= totalPages || loading}
              onClick={() => setPage((p) => p + 1)} data-testid="oracle-next">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>
      </>}

      <EventDetailDialog
        item={detail}
        onClose={() => setDetail(null)}
        onFilterIp={filterByIp}
        ipInfo={ipInfo}
        ipLoading={ipLoading}
        onBan={banIp}
        onUnban={unbanIp}
        banBusy={banBusy}
        onOpenProfile={(ip) => { setDetail(null); setProfileIp(ip); }}
      />

      <IpProfileDialog
        ip={profileIp}
        onClose={() => setProfileIp(null)}
        onBan={banIp}
        onUnban={unbanIp}
        banBusy={banBusy}
      />

      {/* Auto-ban shield config */}
      <Dialog open={autobanOpen} onOpenChange={setAutobanOpen}>
        <DialogContent className="max-w-lg" data-testid="oracle-autoban-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-emerald-600" /> Bouclier auto-bannissement
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            <p className="text-sm text-zinc-500">
              Bannit automatiquement les IP qui se comportent de façon malveillante. Sécurité intégrée : une IP qui s'est déjà connectée avec succès n'est <strong>jamais</strong> auto-bannie (protège votre accès), et les IP privées sont ignorées.
            </p>

            <div className="flex items-center justify-between border border-zinc-100 rounded-lg p-3">
              <div>
                <Label className="text-sm">Activer le bouclier</Label>
                <p className="text-xs text-zinc-500 mt-0.5">Bannissement automatique global.</p>
              </div>
              <Switch checked={!!autoban.enabled} disabled={autobanSaving} onCheckedChange={(v) => saveAutoban({ enabled: v })} data-testid="autoban-enabled-switch" />
            </div>

            <div className={`space-y-4 transition-opacity ${autoban.enabled ? '' : 'opacity-40 pointer-events-none'}`}>
              <div className="flex items-center justify-between border border-zinc-100 rounded-lg p-3">
                <div className="pr-3">
                  <Label className="text-sm">Ban immédiat sur chemin sensible</Label>
                  <p className="text-xs text-zinc-500 mt-0.5">Dès qu'une IP sonde un chemin manifestement hostile (<span className="font-mono">.env, wp-admin, phpmyadmin, .php…</span>).</p>
                </div>
                <Switch checked={!!autoban.sensitive_immediate} disabled={autobanSaving} onCheckedChange={(v) => saveAutoban({ sensitive_immediate: v })} data-testid="autoban-sensitive-switch" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Seuil échecs auth.</Label>
                  <Input type="number" min={3} max={100} value={autoban.auth_threshold}
                    onChange={(e) => setAutoban((a) => ({ ...a, auth_threshold: +e.target.value }))}
                    onBlur={(e) => saveAutoban({ auth_threshold: +e.target.value })}
                    className="mt-1" data-testid="autoban-auth-threshold" />
                </div>
                <div>
                  <Label className="text-xs">Seuil intrusions</Label>
                  <Input type="number" min={3} max={200} value={autoban.intrusion_threshold}
                    onChange={(e) => setAutoban((a) => ({ ...a, intrusion_threshold: +e.target.value }))}
                    onBlur={(e) => saveAutoban({ intrusion_threshold: +e.target.value })}
                    className="mt-1" data-testid="autoban-intrusion-threshold" />
                </div>
                <div>
                  <Label className="text-xs">Fenêtre (heures)</Label>
                  <Input type="number" min={1} max={168} value={autoban.window_hours}
                    onChange={(e) => setAutoban((a) => ({ ...a, window_hours: +e.target.value }))}
                    onBlur={(e) => saveAutoban({ window_hours: +e.target.value })}
                    className="mt-1" data-testid="autoban-window-hours" />
                </div>
              </div>
              <p className="text-[11px] text-zinc-400">
                Une IP est bannie si elle dépasse {autoban.auth_threshold} échecs d'authentification ou {autoban.intrusion_threshold} intrusions sur {autoban.window_hours}h. Les bans automatiques apparaissent dans le journal (événement « Ban automatique ») et dans « IP bannies ».
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={bannedOpen} onOpenChange={setBannedOpen}>
        <DialogContent className="max-w-lg" data-testid="oracle-banned-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2">
              <Ban className="h-5 w-5 text-red-600" /> IP bannies
            </DialogTitle>
          </DialogHeader>
          {bannedList.length === 0 ? (
            <div className="text-sm text-zinc-400 py-6 text-center">Aucune IP bannie pour le moment.</div>
          ) : (
            <div className="space-y-2 max-h-[55vh] overflow-y-auto">
              {bannedList.map((b) => (
                <div key={b.ip} className="flex items-center justify-between gap-3 border border-zinc-100 rounded-md px-3 py-2" data-testid="oracle-banned-row">
                  <div className="min-w-0">
                    <div className="font-mono text-sm text-zinc-800">{b.ip}</div>
                    <div className="text-[11px] text-zinc-400 truncate">
                      {(b.created_at || '').replace('T', ' ').slice(0, 16)}
                      {b.reason ? ` · ${b.reason}` : ''}
                      {b.banned_by ? ` · par ${b.banned_by}` : ''}
                    </div>
                  </div>
                  <Button variant="outline" size="sm" className="rounded-md shrink-0" disabled={banBusy} onClick={() => unbanIp(b.ip)} data-testid="oracle-banned-unban">
                    Débannir
                  </Button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
